// src/modules/Admin/pages/Leads/Pipeline.js
import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { supabase } from '../../../../config/supabase';
import { useAuth } from '../../../../contexts/AuthContext';
import PermissionGate from '../../../../components/PermissionGate';
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaBuilding,
  FaCalendarAlt,
  FaWhatsapp,
  FaTimes,
  FaPlus,
  FaPaperPlane,
  FaHistory,
  FaUserTie,
  FaGripVertical,
  FaSpinner,
  FaCheck,
  FaExclamationTriangle,
  FaBell,
  FaClock,
  FaClipboard,
  FaCopy,
  FaTrash,
  FaFileUpload,
  FaDownload,
  FaFileAlt,
  FaExclamationCircle,
  FaLock
} from 'react-icons/fa';

// Funções de mascaramento de dados
const maskEmail = (email) => {
  if (!email || email.length < 5) return '***@***.com';
  const [user, domain] = email.split('@');
  const maskedUser = user.substring(0, 2) + '***';
  const maskedDomain = domain ? domain.substring(0, 3) + '***' : '***';
  return `${maskedUser}@${maskedDomain}`;
};

const maskPhone = (phone) => {
  if (!phone || phone.length < 8) return '(**) *****-****';
  const cleaned = phone.replace(/\D/g, '');
  const ddd = cleaned.substring(0, 2);
  const lastDigits = cleaned.slice(-4);
  return `(${ddd}) *****-${lastDigits}`;
};

// ===== CONFIGURAÇÃO DO PIPELINE =====
const PIPELINE_STAGES = [
  { id: 'new', label: 'Novos', color: '#3b82f6' },
  { id: 'contacted', label: 'Contatados', color: '#f59e0b' },
  { id: 'qualified', label: 'Qualificados', color: '#8b5cf6' },
  { id: 'proposal', label: 'Proposta', color: '#06b6d4' },
  { id: 'converted', label: 'Fechados', color: '#10b981' },
  { id: 'lost', label: 'Perdidos', color: '#ef4444' }
];

// ===== STYLED COMPONENTS =====
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 140px);
  min-height: 500px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-shrink: 0;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #111;
`;

const Stats = styled.div`
  display: flex;
  gap: 16px;
`;

const StatBadge = styled.div`
  padding: 8px 16px;
  background: ${props => props.$bg || '#f5f5f5'};
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.$color || '#333'};
`;

const PipelineContainer = styled.div`
  display: flex;
  gap: 16px;
  flex: 1;
  overflow-x: auto;
  padding-bottom: 16px;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f0f0f0;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 4px;
  }
`;

const Column = styled.div`
  flex: 0 0 300px;
  background: #f8f9fa;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  max-height: 100%;
`;

const ColumnHeader = styled.div`
  padding: 16px;
  border-bottom: 3px solid ${props => props.$color};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
`;

const ColumnTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ColumnCount = styled.span`
  background: ${props => props.$color};
  color: white;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 12px;
`;

const ColumnContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #ddd;
    border-radius: 3px;
  }
`;

const LeadCard = styled.div`
  background: white;
  border-radius: 10px;
  padding: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 200ms;
  border: 2px solid transparent;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }

  &:active {
    cursor: grabbing;
  }

  ${props => props.$isDragging && `
    opacity: 0.5;
    transform: rotate(3deg);
  `}

  ${props => props.$isOver && `
    border-color: #3b82f6;
  `}
`;

const LeadName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    color: #999;
    font-size: 12px;
  }
`;

const LeadCompany = styled.div`
  font-size: 12px;
  color: #666;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;

  svg {
    color: #999;
    font-size: 11px;
  }
`;

const LeadMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #f0f0f0;
`;

const LeadDate = styled.span`
  font-size: 11px;
  color: #999;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const LeadAssigned = styled.span`
  font-size: 11px;
  color: #666;
  background: #f0f0f0;
  padding: 3px 8px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    font-size: 10px;
  }
`;

const NotesCount = styled.span`
  font-size: 11px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    font-size: 10px;
  }
`;

// Modal
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #111;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  color: #999;
  cursor: pointer;
  padding: 4px;

  &:hover {
    color: #333;
  }
`;

const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

const ModalSection = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    color: #666;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InfoLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #999;
  text-transform: uppercase;
`;

const InfoValue = styled.span`
  font-size: 14px;
  color: #333;
  display: flex;
  align-items: center;
  gap: 8px;

  a {
    color: #3b82f6;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  color: #333;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #111;
  }
`;

const StatusSelect = styled(Select)`
  background: ${props => props.$bg};
  color: white;
  border-color: ${props => props.$bg};
  font-weight: 600;

  option {
    background: white;
    color: #333;
  }
`;

const MessageBox = styled.div`
  background: #f8f9fa;
  border-radius: 10px;
  padding: 14px;
  font-size: 14px;
  color: #333;
  line-height: 1.5;
  white-space: pre-wrap;
`;

const NotesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const NoteItem = styled.div`
  background: #f8f9fa;
  border-radius: 10px;
  padding: 14px;
  border-left: 3px solid #3b82f6;
`;

const NoteContent = styled.p`
  font-size: 14px;
  color: #333;
  line-height: 1.5;
  margin-bottom: 8px;
  white-space: pre-wrap;
`;

const NoteMeta = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #999;
`;

const AddNoteForm = styled.form`
  display: flex;
  gap: 10px;
  margin-top: 12px;
`;

const NoteInput = styled.textarea`
  flex: 1;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  resize: none;
  min-height: 60px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #111;
  }
`;

const AddNoteButton = styled.button`
  padding: 12px 16px;
  background: #111;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  align-self: flex-end;

  &:hover:not(:disabled) {
    background: #333;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const WhatsAppButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #25d366;
  color: white;
  border-radius: 8px;
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;
  margin-top: 12px;

  &:hover {
    background: #128c7e;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 30px 20px;
  color: #999;
  font-size: 13px;
`;

// Tabs para o modal
const ModalTabs = styled.div`
  display: flex;
  border-bottom: 1px solid #eee;
  padding: 0 24px;
`;

const ModalTab = styled.button`
  padding: 12px 16px;
  border: none;
  background: none;
  font-size: 13px;
  font-weight: 500;
  color: ${props => props.$active ? '#111' : '#666'};
  cursor: pointer;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 2px;
    background: ${props => props.$active ? '#111' : 'transparent'};
  }

  &:hover {
    color: #111;
  }

  svg {
    margin-right: 6px;
  }
`;

// Lembretes
const RemindersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ReminderItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: ${props => props.$completed ? '#f9fafb' : props.$overdue ? '#fef2f2' : '#fffbeb'};
  border-radius: 8px;
  border-left: 3px solid ${props => props.$completed ? '#9ca3af' : props.$overdue ? '#ef4444' : '#f59e0b'};
`;

const ReminderCheck = styled.button`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid ${props => props.$completed ? '#10b981' : '#d1d5db'};
  background: ${props => props.$completed ? '#10b981' : 'white'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  font-size: 10px;

  &:hover {
    border-color: #10b981;
  }
`;

const ReminderContent = styled.div`
  flex: 1;
`;

const ReminderTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.$completed ? '#9ca3af' : '#111'};
  text-decoration: ${props => props.$completed ? 'line-through' : 'none'};
  margin-bottom: 4px;
`;

const ReminderMeta = styled.div`
  font-size: 12px;
  color: ${props => props.$overdue ? '#ef4444' : '#666'};
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ReminderDelete = styled.button`
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 4px;
  font-size: 12px;

  &:hover {
    color: #ef4444;
  }
`;

const AddReminderForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: #f9fafb;
  border-radius: 10px;
  margin-bottom: 16px;
`;

const ReminderInputRow = styled.div`
  display: flex;
  gap: 10px;

  @media (max-width: 500px) {
    flex-direction: column;
  }
`;

const ReminderInput = styled.input`
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #111;
  }
`;

const ReminderDateInput = styled.input`
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  min-width: 180px;

  &:focus {
    outline: none;
    border-color: #111;
  }
`;

const AddButton = styled.button`
  padding: 10px 16px;
  background: #111;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #333;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// Templates
const TemplatesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
`;

const TemplateCard = styled.div`
  background: #f9fafb;
  border-radius: 10px;
  padding: 14px;
  cursor: pointer;
  transition: all 200ms;
  border: 2px solid transparent;

  &:hover {
    border-color: #3b82f6;
    background: #f0f7ff;
  }
`;

const TemplateCategory = styled.span`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  color: #666;
  background: #e5e7eb;
  padding: 2px 8px;
  border-radius: 4px;
  display: inline-block;
  margin-bottom: 8px;
`;

const TemplateName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111;
  margin-bottom: 6px;
`;

const TemplatePreview = styled.div`
  font-size: 12px;
  color: #666;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TemplateActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 10px;
`;

const TemplateActionBtn = styled.button`
  flex: 1;
  padding: 8px;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition: all 200ms;

  ${props => props.$primary ? `
    background: #111;
    color: white;
    &:hover { background: #333; }
  ` : `
    background: #e5e7eb;
    color: #333;
    &:hover { background: #d1d5db; }
  `}
`;

const TemplateExpanded = styled.div`
  background: #f9fafb;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 16px;
`;

const TemplateExpandedContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 14px;
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
  margin-bottom: 12px;
  border: 1px solid #e5e7eb;
`;

const CopiedToast = styled.div`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  background: #111;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 2000;
  animation: fadeInUp 0.3s ease;

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }
`;

const ReminderBadge = styled.span`
  position: absolute;
  top: -4px;
  right: -4px;
  background: #ef4444;
  color: white;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 10px;
  min-width: 16px;
  text-align: center;
`;

const LeadReminders = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

// Import CSV
const ImportButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 200ms;

  &:hover {
    background: #059669;
  }
`;

const ImportModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 20px;
`;

const ImportModal = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ImportModalHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ImportModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #111;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ImportModalBody = styled.div`
  padding: 24px;
  overflow-y: auto;
`;

const DropZone = styled.div`
  border: 2px dashed ${props => props.$isDragging ? '#3b82f6' : '#e0e0e0'};
  border-radius: 12px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 200ms;
  background: ${props => props.$isDragging ? '#eff6ff' : '#fafafa'};

  &:hover {
    border-color: #3b82f6;
    background: #eff6ff;
  }
`;

const DropZoneIcon = styled.div`
  font-size: 40px;
  color: #3b82f6;
  margin-bottom: 16px;
`;

const DropZoneText = styled.div`
  font-size: 14px;
  color: #333;
  margin-bottom: 8px;
`;

const DropZoneSubtext = styled.div`
  font-size: 12px;
  color: #999;
`;

const FileSelected = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #f0fdf4;
  border-radius: 8px;
  border: 1px solid #bbf7d0;
`;

const FileIcon = styled.div`
  width: 40px;
  height: 40px;
  background: #10b981;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
`;

const FileInfo = styled.div`
  flex: 1;

  .name {
    font-size: 14px;
    font-weight: 600;
    color: #111;
  }

  .size {
    font-size: 12px;
    color: #666;
  }
`;

const RemoveFileBtn = styled.button`
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 4px;

  &:hover {
    color: #ef4444;
  }
`;

const PreviewSection = styled.div`
  margin-top: 20px;
`;

const PreviewTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
`;

const PreviewTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;

  th, td {
    padding: 8px 12px;
    text-align: left;
    border-bottom: 1px solid #eee;
  }

  th {
    background: #f9fafb;
    font-weight: 600;
    color: #666;
  }

  td {
    color: #333;
  }
`;

const ImportStats = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 16px;
  padding: 16px;
  background: #f9fafb;
  border-radius: 8px;
`;

const ImportStat = styled.div`
  text-align: center;

  .value {
    font-size: 24px;
    font-weight: 700;
    color: ${props => props.$color || '#111'};
  }

  .label {
    font-size: 12px;
    color: #666;
  }
`;

const ImportActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  justify-content: flex-end;
`;

const ImportActionBtn = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 200ms;

  ${props => props.$primary ? `
    background: #111;
    color: white;
    border: none;
    &:hover:not(:disabled) { background: #333; }
  ` : `
    background: white;
    color: #666;
    border: 1px solid #e0e0e0;
    &:hover { background: #f5f5f5; }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ImportResult = styled.div`
  padding: 20px;
  border-radius: 10px;
  background: ${props => props.$success ? '#f0fdf4' : '#fef2f2'};
  border: 1px solid ${props => props.$success ? '#bbf7d0' : '#fecaca'};
  text-align: center;

  .icon {
    font-size: 40px;
    color: ${props => props.$success ? '#10b981' : '#ef4444'};
    margin-bottom: 12px;
  }

  .title {
    font-size: 16px;
    font-weight: 600;
    color: #111;
    margin-bottom: 8px;
  }

  .message {
    font-size: 14px;
    color: #666;
  }
`;

const DownloadTemplateBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #f3f4f6;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  color: #374151;
  cursor: pointer;
  margin-top: 12px;

  &:hover {
    background: #e5e7eb;
  }
`;

const ImportWarning = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px;
  background: #fffbeb;
  border-radius: 8px;
  border: 1px solid #fde68a;
  margin-top: 16px;
  font-size: 12px;
  color: #92400e;

  svg {
    color: #f59e0b;
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px;
  color: #666;

  svg {
    animation: spin 1s linear infinite;
    margin-right: 10px;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

// ===== COMPONENTE PRINCIPAL =====
const Pipeline = () => {
  const { user, hasPermission } = useAuth();
  const [leads, setLeads] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [notes, setNotes] = useState({});
  const [reminders, setReminders] = useState({});
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [modalTab, setModalTab] = useState('info'); // 'info', 'reminders', 'templates'
  const [newNote, setNewNote] = useState('');
  const [newReminder, setNewReminder] = useState({ title: '', date: '' });
  const [saving, setSaving] = useState(false);
  const [draggedLead, setDraggedLead] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [showCopied, setShowCopied] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Estados para importação CSV
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importData, setImportData] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);

  // Verificar permissões
  const canViewFullContact = hasPermission('leads.view_full_contact');
  const canUpdateStatus = hasPermission('leads.update_status');
  const canViewAll = hasPermission('leads.view_all');
  const canAssign = hasPermission('leads.assign');
  const canExport = hasPermission('leads.export');
  const canAddNote = hasPermission('leads.add_note');
  const canAddReminder = hasPermission('leads.add_reminder');

  // Carregar dados
  useEffect(() => {
    fetchLeads();
    fetchAdmins();
    fetchTemplates();
  }, [user]);

  const fetchLeads = async () => {
    try {
      let query = supabase
        .from('leads')
        .select('*')
        .order('created_at', { ascending: false });

      // Se não pode ver todos, filtrar apenas os atribuídos ao usuário
      if (!canViewAll && user?.email) {
        query = query.eq('assigned_to', user.email);
      }

      const { data, error } = await query;

      if (error) throw error;
      setLeads(data || []);
    } catch (error) {
      console.error('Erro ao carregar leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdmins = async () => {
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .select('*');

      if (error) throw error;
      setAdmins(data || []);
    } catch (error) {
      console.error('Erro ao carregar admins:', error);
    }
  };

  const fetchNotes = async (leadId) => {
    try {
      const { data, error } = await supabase
        .from('lead_notes')
        .select('*')
        .eq('lead_id', leadId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setNotes(prev => ({ ...prev, [leadId]: data || [] }));
    } catch (error) {
      console.error('Erro ao carregar notas:', error);
    }
  };

  const fetchReminders = async (leadId) => {
    try {
      const { data, error } = await supabase
        .from('lead_reminders')
        .select('*')
        .eq('lead_id', leadId)
        .order('reminder_date', { ascending: true });

      if (error) throw error;
      setReminders(prev => ({ ...prev, [leadId]: data || [] }));
    } catch (error) {
      console.error('Erro ao carregar lembretes:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('message_templates')
        .select('*')
        .order('category', { ascending: true });

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
    }
  };

  const addReminder = async (e) => {
    e.preventDefault();
    if (!newReminder.title.trim() || !newReminder.date || !selectedLead) return;

    setSaving(true);
    try {
      const { data, error } = await supabase
        .from('lead_reminders')
        .insert({
          lead_id: selectedLead.id,
          title: newReminder.title.trim(),
          reminder_date: new Date(newReminder.date).toISOString(),
          created_by: user?.email
        })
        .select()
        .single();

      if (error) throw error;

      setReminders(prev => ({
        ...prev,
        [selectedLead.id]: [...(prev[selectedLead.id] || []), data].sort(
          (a, b) => new Date(a.reminder_date) - new Date(b.reminder_date)
        )
      }));
      setNewReminder({ title: '', date: '' });
    } catch (error) {
      console.error('Erro ao adicionar lembrete:', error);
    } finally {
      setSaving(false);
    }
  };

  const toggleReminderComplete = async (reminderId, currentStatus) => {
    try {
      const { error } = await supabase
        .from('lead_reminders')
        .update({
          completed: !currentStatus,
          completed_at: !currentStatus ? new Date().toISOString() : null
        })
        .eq('id', reminderId);

      if (error) throw error;

      setReminders(prev => ({
        ...prev,
        [selectedLead.id]: (prev[selectedLead.id] || []).map(r =>
          r.id === reminderId ? { ...r, completed: !currentStatus } : r
        )
      }));
    } catch (error) {
      console.error('Erro ao atualizar lembrete:', error);
    }
  };

  const deleteReminder = async (reminderId) => {
    try {
      const { error } = await supabase
        .from('lead_reminders')
        .delete()
        .eq('id', reminderId);

      if (error) throw error;

      setReminders(prev => ({
        ...prev,
        [selectedLead.id]: (prev[selectedLead.id] || []).filter(r => r.id !== reminderId)
      }));
    } catch (error) {
      console.error('Erro ao excluir lembrete:', error);
    }
  };

  const applyTemplateVariables = (content, lead) => {
    return content
      .replace(/\{nome\}/gi, lead.name || '')
      .replace(/\{empresa\}/gi, lead.company || '')
      .replace(/\{email\}/gi, canViewFullContact ? (lead.email || '') : maskEmail(lead.email))
      .replace(/\{telefone\}/gi, canViewFullContact ? (lead.phone || '') : maskPhone(lead.phone));
  };

  const copyTemplateToClipboard = async (template) => {
    const content = applyTemplateVariables(template.content, selectedLead);
    try {
      await navigator.clipboard.writeText(content);
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000);
    } catch (error) {
      console.error('Erro ao copiar:', error);
    }
  };

  const openWhatsAppWithTemplate = (template) => {
    if (!selectedLead?.phone) return;
    const content = applyTemplateVariables(template.content, selectedLead);
    const phone = selectedLead.phone.replace(/\D/g, '');
    const formattedPhone = phone.startsWith('55') ? phone : `55${phone}`;
    const encodedMessage = encodeURIComponent(content);
    window.open(`https://wa.me/${formattedPhone}?text=${encodedMessage}`, '_blank');
  };

  const isReminderOverdue = (reminderDate) => {
    return new Date(reminderDate) < new Date() && !reminders.completed;
  };

  const getPendingRemindersCount = (leadId) => {
    const leadReminders = reminders[leadId] || [];
    return leadReminders.filter(r => !r.completed).length;
  };

  // ===== FUNÇÕES DE IMPORTAÇÃO CSV =====
  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];

    // Detectar separador (vírgula ou ponto e vírgula)
    const firstLine = lines[0];
    const separator = firstLine.includes(';') ? ';' : ',';

    const headers = lines[0].split(separator).map(h => h.trim().toLowerCase().replace(/"/g, ''));
    const data = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(separator).map(v => v.trim().replace(/"/g, ''));
      const row = {};

      headers.forEach((header, index) => {
        // Mapear diferentes nomes de colunas
        if (['nome', 'name', 'nome completo'].includes(header)) {
          row.name = values[index] || '';
        } else if (['email', 'e-mail', 'correio'].includes(header)) {
          row.email = values[index] || '';
        } else if (['telefone', 'phone', 'tel', 'celular', 'whatsapp'].includes(header)) {
          row.phone = values[index] || '';
        } else if (['empresa', 'company', 'companhia', 'organizacao'].includes(header)) {
          row.company = values[index] || '';
        } else if (['mensagem', 'message', 'observacao', 'obs', 'notas'].includes(header)) {
          row.message = values[index] || '';
        } else if (['origem', 'source', 'fonte', 'canal'].includes(header)) {
          row.source = values[index] || '';
        }
      });

      // Só adicionar se tiver pelo menos email ou nome
      if (row.email || row.name) {
        row.status = 'new';
        row.source = row.source || 'CSV Import';
        data.push(row);
      }
    }

    return data;
  };

  const handleFileSelect = (file) => {
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      alert('Por favor, selecione um arquivo CSV');
      return;
    }

    setImportFile(file);
    setImportResult(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const parsed = parseCSV(text);
      setImportData(parsed);
    };
    reader.readAsText(file, 'UTF-8');
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    setIsDraggingFile(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    handleFileSelect(file);
  };

  const downloadTemplate = () => {
    const csvContent = 'nome,email,telefone,empresa,mensagem,origem\nJoão Silva,joao@empresa.com,11999999999,Empresa ABC,Interessado em automação,Site\nMaria Santos,maria@exemplo.com,21988888888,XYZ Ltda,Quer saber mais sobre IA,LinkedIn';
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'template_leads.csv';
    link.click();
  };

  const importLeads = async () => {
    if (importData.length === 0) return;

    setImporting(true);
    let imported = 0;
    let errors = 0;

    try {
      for (const lead of importData) {
        try {
          const { error } = await supabase
            .from('leads')
            .insert({
              name: lead.name || null,
              email: lead.email || null,
              phone: lead.phone || null,
              company: lead.company || null,
              message: lead.message || null,
              source: lead.source || 'CSV Import',
              status: 'new'
            });

          if (error) {
            console.error('Erro ao importar lead:', error);
            errors++;
          } else {
            imported++;
          }
        } catch (e) {
          errors++;
        }
      }

      setImportResult({
        success: true,
        imported,
        errors,
        total: importData.length
      });

      // Recarregar leads
      fetchLeads();
    } catch (error) {
      console.error('Erro na importação:', error);
      setImportResult({
        success: false,
        message: 'Erro ao importar leads'
      });
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setImportFile(null);
    setImportData([]);
    setImportResult(null);
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    resetImport();
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      const { error } = await supabase
        .from('leads')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', leadId);

      if (error) throw error;

      setLeads(prev => prev.map(lead =>
        lead.id === leadId ? { ...lead, status: newStatus } : lead
      ));
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
    }
  };

  const updateLeadAssigned = async (leadId, email, name) => {
    try {
      const { error } = await supabase
        .from('leads')
        .update({
          assigned_to: email,
          assigned_name: name,
          updated_at: new Date().toISOString()
        })
        .eq('id', leadId);

      if (error) throw error;

      setLeads(prev => prev.map(lead =>
        lead.id === leadId ? { ...lead, assigned_to: email, assigned_name: name } : lead
      ));

      if (selectedLead?.id === leadId) {
        setSelectedLead(prev => ({ ...prev, assigned_to: email, assigned_name: name }));
      }
    } catch (error) {
      console.error('Erro ao atribuir responsável:', error);
    }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim() || !selectedLead) return;

    setSaving(true);
    try {
      const { data, error } = await supabase
        .from('lead_notes')
        .insert({
          lead_id: selectedLead.id,
          content: newNote.trim(),
          author_email: user?.email,
          author_name: user?.user_metadata?.full_name || user?.email
        })
        .select()
        .single();

      if (error) throw error;

      setNotes(prev => ({
        ...prev,
        [selectedLead.id]: [data, ...(prev[selectedLead.id] || [])]
      }));
      setNewNote('');
    } catch (error) {
      console.error('Erro ao adicionar nota:', error);
    } finally {
      setSaving(false);
    }
  };

  const openLeadModal = (lead) => {
    setSelectedLead(lead);
    setModalTab('info');
    setSelectedTemplate(null);
    if (!notes[lead.id]) {
      fetchNotes(lead.id);
    }
    if (!reminders[lead.id]) {
      fetchReminders(lead.id);
    }
  };

  // Drag and Drop
  const handleDragStart = (e, lead) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, stageId) => {
    e.preventDefault();
    setDragOverColumn(stageId);
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = (e, newStatus) => {
    e.preventDefault();
    if (draggedLead && draggedLead.status !== newStatus) {
      updateLeadStatus(draggedLead.id, newStatus);
    }
    setDraggedLead(null);
    setDragOverColumn(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit'
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getWhatsAppLink = (phone) => {
    if (!phone) return null;
    const cleanPhone = phone.replace(/\D/g, '');
    const formattedPhone = cleanPhone.startsWith('55') ? cleanPhone : `55${cleanPhone}`;
    return `https://wa.me/${formattedPhone}`;
  };

  const getLeadsByStage = (stageId) => {
    return leads.filter(lead => lead.status === stageId);
  };

  const getStageColor = (stageId) => {
    return PIPELINE_STAGES.find(s => s.id === stageId)?.color || '#666';
  };

  const totalLeads = leads.length;
  const convertedLeads = leads.filter(l => l.status === 'converted').length;

  if (loading) {
    return (
      <Container>
        <LoadingState>
          <FaSpinner /> Carregando pipeline...
        </LoadingState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Pipeline de Vendas</Title>
        <Stats>
          {canExport && (
            <ImportButton onClick={() => setShowImportModal(true)}>
              <FaFileUpload /> Importar CSV
            </ImportButton>
          )}
          <StatBadge>
            {totalLeads} leads
          </StatBadge>
          <StatBadge $bg="#ecfdf5" $color="#065f46">
            {convertedLeads} fechados
          </StatBadge>
        </Stats>
      </Header>

      <PipelineContainer>
        {PIPELINE_STAGES.map(stage => {
          const stageLeads = getLeadsByStage(stage.id);
          return (
            <Column
              key={stage.id}
              onDragOver={(e) => handleDragOver(e, stage.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, stage.id)}
              style={dragOverColumn === stage.id ? { background: '#e8f4ff' } : {}}
            >
              <ColumnHeader $color={stage.color}>
                <ColumnTitle>{stage.label}</ColumnTitle>
                <ColumnCount $color={stage.color}>{stageLeads.length}</ColumnCount>
              </ColumnHeader>

              <ColumnContent>
                {stageLeads.length === 0 ? (
                  <EmptyState>Nenhum lead</EmptyState>
                ) : (
                  stageLeads.map(lead => (
                    <LeadCard
                      key={lead.id}
                      draggable={canUpdateStatus}
                      onDragStart={(e) => canUpdateStatus && handleDragStart(e, lead)}
                      onClick={() => openLeadModal(lead)}
                      $isDragging={draggedLead?.id === lead.id}
                      style={{ cursor: canUpdateStatus ? 'grab' : 'pointer' }}
                    >
                      <LeadName>
                        <FaGripVertical />
                        {lead.name || 'Sem nome'}
                      </LeadName>
                      {lead.company && (
                        <LeadCompany>
                          <FaBuilding />
                          {lead.company}
                        </LeadCompany>
                      )}
                      <LeadMeta>
                        <LeadDate>
                          <FaCalendarAlt />
                          {formatDate(lead.created_at)}
                        </LeadDate>
                        {lead.assigned_name ? (
                          <LeadAssigned>
                            <FaUserTie />
                            {lead.assigned_name.split(' ')[0]}
                          </LeadAssigned>
                        ) : (
                          <LeadAssigned style={{ background: '#fef3c7', color: '#92400e' }}>
                            <FaExclamationTriangle />
                            Sem dono
                          </LeadAssigned>
                        )}
                      </LeadMeta>
                    </LeadCard>
                  ))
                )}
              </ColumnContent>
            </Column>
          );
        })}
      </PipelineContainer>

      {/* Modal de Detalhes */}
      {selectedLead && (
        <ModalOverlay onClick={() => setSelectedLead(null)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{selectedLead.name || 'Lead sem nome'}</ModalTitle>
              <CloseButton onClick={() => setSelectedLead(null)}>
                <FaTimes />
              </CloseButton>
            </ModalHeader>

            {/* Tabs */}
            <ModalTabs>
              <ModalTab $active={modalTab === 'info'} onClick={() => setModalTab('info')}>
                <FaUser /> Informações
              </ModalTab>
              <ModalTab $active={modalTab === 'reminders'} onClick={() => setModalTab('reminders')}>
                <FaBell /> Lembretes
                {(reminders[selectedLead.id] || []).filter(r => !r.completed).length > 0 && (
                  <span style={{
                    background: '#ef4444',
                    color: 'white',
                    fontSize: 10,
                    padding: '2px 6px',
                    borderRadius: 10,
                    marginLeft: 6
                  }}>
                    {(reminders[selectedLead.id] || []).filter(r => !r.completed).length}
                  </span>
                )}
              </ModalTab>
              <ModalTab $active={modalTab === 'templates'} onClick={() => setModalTab('templates')}>
                <FaClipboard /> Templates
              </ModalTab>
            </ModalTabs>

            <ModalBody>
              {/* ===== TAB: INFORMAÇÕES ===== */}
              {modalTab === 'info' && (
                <>
                  {/* Informações do Lead */}
                  <ModalSection>
                    <SectionTitle><FaUser /> Informações</SectionTitle>
                    <InfoGrid>
                      <InfoItem>
                        <InfoLabel>
                          Email {!canViewFullContact && <FaLock style={{ fontSize: 10, marginLeft: 4, color: '#999' }} />}
                        </InfoLabel>
                        <InfoValue>
                          <FaEnvelope style={{ color: '#999', fontSize: 12 }} />
                          {canViewFullContact ? (
                            <a href={`mailto:${selectedLead.email}`}>{selectedLead.email}</a>
                          ) : (
                            <span style={{ color: '#666' }}>{maskEmail(selectedLead.email)}</span>
                          )}
                        </InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>
                          Telefone {!canViewFullContact && <FaLock style={{ fontSize: 10, marginLeft: 4, color: '#999' }} />}
                        </InfoLabel>
                        <InfoValue>
                          <FaPhone style={{ color: '#999', fontSize: 12 }} />
                          {canViewFullContact ? (selectedLead.phone || '-') : maskPhone(selectedLead.phone)}
                        </InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>Empresa</InfoLabel>
                        <InfoValue>
                          <FaBuilding style={{ color: '#999', fontSize: 12 }} />
                          {selectedLead.company || '-'}
                        </InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>Data de entrada</InfoLabel>
                        <InfoValue>
                          <FaCalendarAlt style={{ color: '#999', fontSize: 12 }} />
                          {formatDateTime(selectedLead.created_at)}
                        </InfoValue>
                      </InfoItem>
                    </InfoGrid>

                    {selectedLead.phone && canViewFullContact && (
                      <WhatsAppButton href={getWhatsAppLink(selectedLead.phone)} target="_blank">
                        <FaWhatsapp />
                        Abrir WhatsApp
                      </WhatsAppButton>
                    )}
                  </ModalSection>

                  {/* Status e Responsável */}
                  <ModalSection>
                    <SectionTitle><FaUserTie /> Gestão</SectionTitle>
                    <InfoGrid>
                      <InfoItem>
                        <InfoLabel>Status</InfoLabel>
                        {canUpdateStatus ? (
                          <StatusSelect
                            value={selectedLead.status}
                            onChange={(e) => {
                              updateLeadStatus(selectedLead.id, e.target.value);
                              setSelectedLead(prev => ({ ...prev, status: e.target.value }));
                            }}
                            $bg={getStageColor(selectedLead.status)}
                          >
                            {PIPELINE_STAGES.map(stage => (
                              <option key={stage.id} value={stage.id}>{stage.label}</option>
                            ))}
                          </StatusSelect>
                        ) : (
                          <InfoValue style={{
                            background: getStageColor(selectedLead.status),
                            color: 'white',
                            padding: '8px 12px',
                            borderRadius: 8,
                            fontWeight: 600
                          }}>
                            {PIPELINE_STAGES.find(s => s.id === selectedLead.status)?.label || selectedLead.status}
                          </InfoValue>
                        )}
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>Responsável</InfoLabel>
                        {canAssign ? (
                          <Select
                            value={selectedLead.assigned_to || ''}
                            onChange={(e) => {
                              const admin = admins.find(a => a.email === e.target.value);
                              updateLeadAssigned(selectedLead.id, e.target.value, admin?.name || e.target.value);
                            }}
                          >
                            <option value="">Selecionar...</option>
                            {admins.map(admin => (
                              <option key={admin.id} value={admin.email}>
                                {admin.name || admin.email}
                              </option>
                            ))}
                          </Select>
                        ) : (
                          <InfoValue>
                            {selectedLead.assigned_name || selectedLead.assigned_to || 'Não atribuído'}
                          </InfoValue>
                        )}
                      </InfoItem>
                    </InfoGrid>
                  </ModalSection>

                  {/* Mensagem original */}
                  {selectedLead.message && (
                    <ModalSection>
                      <SectionTitle><FaEnvelope /> Mensagem do Lead</SectionTitle>
                      <MessageBox>{selectedLead.message}</MessageBox>
                    </ModalSection>
                  )}

                  {/* Notas / Histórico */}
                  <ModalSection>
                    <SectionTitle><FaHistory /> Histórico de Interações</SectionTitle>

                    {canAddNote && (
                      <AddNoteForm onSubmit={addNote}>
                        <NoteInput
                          placeholder="Adicionar nota... (ex: Liguei, vai avaliar proposta)"
                          value={newNote}
                          onChange={(e) => setNewNote(e.target.value)}
                        />
                        <AddNoteButton type="submit" disabled={!newNote.trim() || saving}>
                          {saving ? <FaSpinner className="spin" /> : <FaPaperPlane />}
                          Adicionar
                        </AddNoteButton>
                      </AddNoteForm>
                    )}

                    <NotesContainer style={{ marginTop: 16 }}>
                      {(notes[selectedLead.id] || []).length === 0 ? (
                        <EmptyState>Nenhuma nota ainda</EmptyState>
                      ) : (
                        (notes[selectedLead.id] || []).map(note => (
                          <NoteItem key={note.id}>
                            <NoteContent>{note.content}</NoteContent>
                            <NoteMeta>
                              <span>{note.author_name || note.author_email}</span>
                              <span>{formatDateTime(note.created_at)}</span>
                            </NoteMeta>
                          </NoteItem>
                        ))
                      )}
                    </NotesContainer>
                  </ModalSection>
                </>
              )}

              {/* ===== TAB: LEMBRETES ===== */}
              {modalTab === 'reminders' && (
                <>
                  {canAddReminder && (
                    <AddReminderForm onSubmit={addReminder}>
                      <SectionTitle style={{ margin: 0 }}><FaPlus /> Novo Lembrete</SectionTitle>
                      <ReminderInputRow>
                        <ReminderInput
                          type="text"
                          placeholder="Ex: Ligar para acompanhar proposta"
                          value={newReminder.title}
                          onChange={(e) => setNewReminder(prev => ({ ...prev, title: e.target.value }))}
                        />
                        <ReminderDateInput
                          type="datetime-local"
                          value={newReminder.date}
                          onChange={(e) => setNewReminder(prev => ({ ...prev, date: e.target.value }))}
                        />
                      </ReminderInputRow>
                      <AddButton type="submit" disabled={!newReminder.title.trim() || !newReminder.date || saving}>
                        {saving ? <FaSpinner /> : <FaPlus />}
                        Adicionar Lembrete
                      </AddButton>
                    </AddReminderForm>
                  )}

                  <RemindersList>
                    {(reminders[selectedLead.id] || []).length === 0 ? (
                      <EmptyState>Nenhum lembrete programado</EmptyState>
                    ) : (
                      (reminders[selectedLead.id] || []).map(reminder => {
                        const overdue = !reminder.completed && new Date(reminder.reminder_date) < new Date();
                        return (
                          <ReminderItem key={reminder.id} $completed={reminder.completed} $overdue={overdue}>
                            <ReminderCheck
                              $completed={reminder.completed}
                              onClick={() => toggleReminderComplete(reminder.id, reminder.completed)}
                            >
                              {reminder.completed && <FaCheck />}
                            </ReminderCheck>
                            <ReminderContent>
                              <ReminderTitle $completed={reminder.completed}>
                                {reminder.title}
                              </ReminderTitle>
                              <ReminderMeta $overdue={overdue}>
                                <FaClock />
                                {formatDateTime(reminder.reminder_date)}
                                {overdue && ' (Atrasado!)'}
                              </ReminderMeta>
                            </ReminderContent>
                            <ReminderDelete onClick={() => deleteReminder(reminder.id)}>
                              <FaTrash />
                            </ReminderDelete>
                          </ReminderItem>
                        );
                      })
                    )}
                  </RemindersList>
                </>
              )}

              {/* ===== TAB: TEMPLATES ===== */}
              {modalTab === 'templates' && (
                <>
                  {selectedTemplate ? (
                    <TemplateExpanded>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <div>
                          <TemplateCategory>{selectedTemplate.category}</TemplateCategory>
                          <TemplateName style={{ marginTop: 4 }}>{selectedTemplate.name}</TemplateName>
                        </div>
                        <CloseButton onClick={() => setSelectedTemplate(null)}>
                          <FaTimes />
                        </CloseButton>
                      </div>
                      <TemplateExpandedContent>
                        {applyTemplateVariables(selectedTemplate.content, selectedLead)}
                      </TemplateExpandedContent>
                      <TemplateActions>
                        <TemplateActionBtn onClick={() => copyTemplateToClipboard(selectedTemplate)}>
                          <FaCopy /> Copiar
                        </TemplateActionBtn>
                        {selectedLead.phone && canViewFullContact && (
                          <TemplateActionBtn $primary onClick={() => openWhatsAppWithTemplate(selectedTemplate)}>
                            <FaWhatsapp /> Enviar WhatsApp
                          </TemplateActionBtn>
                        )}
                      </TemplateActions>
                    </TemplateExpanded>
                  ) : (
                    <>
                      <SectionTitle><FaClipboard /> Escolha um Template</SectionTitle>
                      <p style={{ fontSize: 12, color: '#666', marginBottom: 16 }}>
                        Variáveis disponíveis: {'{nome}'}, {'{empresa}'}, {'{email}'}, {'{telefone}'}
                      </p>
                      {templates.length === 0 ? (
                        <EmptyState>
                          Nenhum template cadastrado.<br />
                          Cadastre templates em Configurações → Leads
                        </EmptyState>
                      ) : (
                        <TemplatesGrid>
                          {templates.map(template => (
                            <TemplateCard key={template.id} onClick={() => setSelectedTemplate(template)}>
                              <TemplateCategory>{template.category}</TemplateCategory>
                              <TemplateName>{template.name}</TemplateName>
                              <TemplatePreview>{template.content}</TemplatePreview>
                            </TemplateCard>
                          ))}
                        </TemplatesGrid>
                      )}
                    </>
                  )}
                </>
              )}
            </ModalBody>
          </Modal>
        </ModalOverlay>
      )}

      {/* Toast de copiado */}
      {showCopied && (
        <CopiedToast>
          <FaCheck /> Copiado para a área de transferência!
        </CopiedToast>
      )}

      {/* Modal de Importação CSV */}
      {showImportModal && (
        <ImportModalOverlay onClick={closeImportModal}>
          <ImportModal onClick={(e) => e.stopPropagation()}>
            <ImportModalHeader>
              <ImportModalTitle>
                <FaFileUpload /> Importar Leads via CSV
              </ImportModalTitle>
              <CloseButton onClick={closeImportModal}>
                <FaTimes />
              </CloseButton>
            </ImportModalHeader>

            <ImportModalBody>
              {importResult ? (
                // Resultado da importação
                <ImportResult $success={importResult.success}>
                  <div className="icon">
                    {importResult.success ? <FaCheck /> : <FaExclamationCircle />}
                  </div>
                  <div className="title">
                    {importResult.success ? 'Importação concluída!' : 'Erro na importação'}
                  </div>
                  <div className="message">
                    {importResult.success
                      ? `${importResult.imported} de ${importResult.total} leads importados com sucesso.${importResult.errors > 0 ? ` ${importResult.errors} com erro.` : ''}`
                      : importResult.message
                    }
                  </div>
                  <ImportActions style={{ justifyContent: 'center' }}>
                    <ImportActionBtn onClick={resetImport}>
                      Importar mais
                    </ImportActionBtn>
                    <ImportActionBtn $primary onClick={closeImportModal}>
                      Fechar
                    </ImportActionBtn>
                  </ImportActions>
                </ImportResult>
              ) : !importFile ? (
                // Seleção de arquivo
                <>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileInputChange}
                    style={{ display: 'none' }}
                    id="csv-upload"
                  />
                  <DropZone
                    $isDragging={isDraggingFile}
                    onClick={() => document.getElementById('csv-upload').click()}
                    onDragOver={(e) => { e.preventDefault(); setIsDraggingFile(true); }}
                    onDragLeave={() => setIsDraggingFile(false)}
                    onDrop={handleFileDrop}
                  >
                    <DropZoneIcon>
                      <FaFileUpload />
                    </DropZoneIcon>
                    <DropZoneText>
                      Arraste um arquivo CSV aqui ou clique para selecionar
                    </DropZoneText>
                    <DropZoneSubtext>
                      Suporta arquivos .csv com separador vírgula ou ponto e vírgula
                    </DropZoneSubtext>
                  </DropZone>

                  <DownloadTemplateBtn onClick={downloadTemplate}>
                    <FaDownload /> Baixar template de exemplo
                  </DownloadTemplateBtn>

                  <ImportWarning>
                    <FaExclamationTriangle />
                    <div>
                      <strong>Colunas aceitas:</strong> nome, email, telefone, empresa, mensagem, origem.<br />
                      O arquivo deve ter uma linha de cabeçalho com os nomes das colunas.
                    </div>
                  </ImportWarning>
                </>
              ) : (
                // Preview dos dados
                <>
                  <FileSelected>
                    <FileIcon>
                      <FaFileAlt />
                    </FileIcon>
                    <FileInfo>
                      <div className="name">{importFile.name}</div>
                      <div className="size">{formatFileSize(importFile.size)}</div>
                    </FileInfo>
                    <RemoveFileBtn onClick={resetImport}>
                      <FaTimes />
                    </RemoveFileBtn>
                  </FileSelected>

                  <ImportStats>
                    <ImportStat>
                      <div className="value">{importData.length}</div>
                      <div className="label">Leads encontrados</div>
                    </ImportStat>
                    <ImportStat $color="#10b981">
                      <div className="value">{importData.filter(l => l.email).length}</div>
                      <div className="label">Com email</div>
                    </ImportStat>
                    <ImportStat $color="#3b82f6">
                      <div className="value">{importData.filter(l => l.phone).length}</div>
                      <div className="label">Com telefone</div>
                    </ImportStat>
                  </ImportStats>

                  {importData.length > 0 && (
                    <PreviewSection>
                      <PreviewTitle>Preview (primeiros 5 registros)</PreviewTitle>
                      <div style={{ overflowX: 'auto' }}>
                        <PreviewTable>
                          <thead>
                            <tr>
                              <th>Nome</th>
                              <th>Email</th>
                              <th>Telefone</th>
                              <th>Empresa</th>
                            </tr>
                          </thead>
                          <tbody>
                            {importData.slice(0, 5).map((lead, index) => (
                              <tr key={index}>
                                <td>{lead.name || '-'}</td>
                                <td>{lead.email || '-'}</td>
                                <td>{lead.phone || '-'}</td>
                                <td>{lead.company || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </PreviewTable>
                      </div>
                    </PreviewSection>
                  )}

                  <ImportActions>
                    <ImportActionBtn onClick={resetImport}>
                      Cancelar
                    </ImportActionBtn>
                    <ImportActionBtn
                      $primary
                      onClick={importLeads}
                      disabled={importing || importData.length === 0}
                    >
                      {importing ? <FaSpinner /> : <FaFileUpload />}
                      {importing ? 'Importando...' : `Importar ${importData.length} leads`}
                    </ImportActionBtn>
                  </ImportActions>
                </>
              )}
            </ImportModalBody>
          </ImportModal>
        </ImportModalOverlay>
      )}
    </Container>
  );
};

export default Pipeline;
